import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { MoneyAmountItem, Space, TextFieldItem } from '@/components';
import { FPSLayout } from '@/layout';
import { MoneyType } from '@/model/common';
import { prepare, saveWithdrawalFormData } from '@/model/withdrawal/actions';
import { makeSelectFormData } from '@/model/withdrawal/selectors';
import { WalletId, WithdrawalUIState } from '@/model/withdrawal/types';
import { BankItem, ReceiverItem, WalletItem } from '@/pages/withdrawal/components';

interface PreparePageViewProps {
  'data-test'?: string;
  readonly?: {
    wallet?: boolean;
  };
}

const selectDemand = makeSelectFormData();

export const PreparePageView: React.FC<PreparePageViewProps> = ({ readonly, 'data-test': dataTest }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const demand = useSelector(selectDemand);
  const { formatMessage } = useIntl();

  return (
    <FPSLayout
      data-test={dataTest && `${dataTest}-layout`}
      height={650}
      minHeight={400}
      buttons={{
        back: `${WithdrawalUIState.RECEIVER}`,
        logout: <FormattedMessage id="withdrawal.logout.button.title" />,
        main: {
          submit: true,
          onAct: async (): Promise<void> => {
            const { error } = await prepare()(dispatch);
            if (!error) {
              history.push(`${WithdrawalUIState.TRANSFER}`);
            }
          },
          disabled:
            // prettier-ignore
            // prettier: FIXME: https://github.com/prettier/prettier/pull/7111
            !demand?.amount?.value
            || demand.amount.hasError
            || !demand.bank?.value
            || demand.bank.hasError
            || !demand.wallet?.value
            || demand.wallet.hasError
            || demand.message?.hasError,
          title: <FormattedMessage id="withdrawal.pages.prepare.submit.title" />,
        },
      }}
      header={{ title: <FormattedMessage id="withdrawal.pages.prepare.title" /> }}
    >
      <Space direction="vertical" size={2}>
        <ReceiverItem
          data-test={dataTest && `${dataTest}-receiver`}
          value={{
            value: demand?.receiver,
            hasError: false,
          }}
          readonly
          shortPhoneMask
        />
        <MoneyAmountItem
          data-test={dataTest && `${dataTest}-money`}
          value={demand?.amount}
          onChange={async (amount?: MoneyType): Promise<void> => {
            await saveWithdrawalFormData({ amount })(dispatch);
          }}
          messages={{
            label: formatMessage({ id: 'withdrawal.fields.amount.label' }),
            error: <FormattedMessage id="withdrawal.fields.amount.error" />,
          }}
        />
        <WalletItem
          data-test={dataTest && `${dataTest}-wallet`}
          value={demand?.wallet}
          onChange={async (wallet?: WalletId): Promise<void> => {
            await saveWithdrawalFormData({ wallet })(dispatch);
          }}
          selectConstraints={{ minBalance: demand?.amount?.value || 0 }}
          readonly={readonly?.wallet}
        />
        <BankItem
          data-test={dataTest && `${dataTest}-bank`}
          value={demand?.bank}
          onChange={async (bank?: string): Promise<void> => {
            await saveWithdrawalFormData({ bank })(dispatch);
          }}
        />
        <TextFieldItem
          data-test={dataTest && `${dataTest}-message`}
          InputProps={{ className: 'ym-hide-content' }}
          value={demand?.message}
          onChange={async (message?: string): Promise<void> => {
            await saveWithdrawalFormData({ message: message || '' })(dispatch);
          }}
          messages={{
            label: <FormattedMessage id="withdrawal.fields.message.label" />,
            error: <FormattedMessage id="withdrawal.fields.message.error" />,
          }}
        />
      </Space>
    </FPSLayout>
  );
};

export default PreparePageView;
